import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { verveContactService } from "@/services/VerveContactService";
import { showSuccessMessage, showApiError } from "@/lib/toast";
import { getDeviceInfo } from "@/utils/auditUtils";

export const useActivityStats = (params = {}) => {
  return useQuery({
    queryKey: ["Verve-activity-stats", params],
    queryFn: () => verveContactService.getActivityStats(params),
  });
};

export const useVerveContacts = (tableParams = {}, statsParams = {}) => {
  const queryClient = useQueryClient();

  // Queries
  const contactsQuery = useQuery({
    queryKey: ["Verve-contacts", tableParams],
    queryFn: () => verveContactService.getContacts(tableParams)
  });

  const importHistoryQuery = useQuery({
    queryKey: ["Verve-import-history"],
    queryFn: () => verveContactService.getImportHistory()
  });

  const statsQuery = useQuery({
    queryKey: ["Verve-contact-stats", statsParams],
    queryFn: () => verveContactService.getStats(statsParams)
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data) => verveContactService.createContact({ ...data, deviceInfo: getDeviceInfo() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["Verve-contacts"] });
      queryClient.invalidateQueries({ queryKey: ["Verve-contact-stats"] });
      showSuccessMessage("Contact created successfully");
    },
    onError: (error) => showApiError(error)
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => verveContactService.updateContact(id, { ...data, deviceInfo: getDeviceInfo() }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["Verve-contacts"] });
      queryClient.invalidateQueries({ queryKey: ["Verve-contact-stats"] });
      queryClient.invalidateQueries({ queryKey: ["contact-activities", variables.id] });
      showSuccessMessage("Contact updated successfully");
    },
    onError: (error) => showApiError(error)
  });

  const deleteMutation = useMutation({
    mutationFn: verveContactService.deleteContact,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["Verve-contacts"] });
      queryClient.invalidateQueries({ queryKey: ["Verve-contact-stats"] });
      showSuccessMessage("Contact deleted successfully");
    },
    onError: (error) => showApiError(error)
  });

  const bulkImportMutation = useMutation({
    mutationFn: ({ contacts, filename }) => verveContactService.bulkImport({
      contacts,
      filename,
      deviceInfo: getDeviceInfo()
    }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["Verve-contacts"] });
      queryClient.invalidateQueries({ queryKey: ["Verve-contact-stats"] });
      queryClient.invalidateQueries({ queryKey: ["Verve-import-history"] });
      if (res.failed === 0) {
        showSuccessMessage(`Import complete: ${res.success} customers imported successfully`);
      }
    },
    onError: (error) => showApiError(error)
  });

  const deleteImportMutation = useMutation({
    mutationFn: verveContactService.deleteImport,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["Verve-contacts"] });
      queryClient.invalidateQueries({ queryKey: ["Verve-import-history"] });
      queryClient.invalidateQueries({ queryKey: ["Verve-contact-stats"] });
      showSuccessMessage("Import and associated contacts removed");
    },
    onError: (error) => showApiError(error)
  });

  return {
    // Data
    contacts: contactsQuery.data?.contacts || [],
    total: contactsQuery.data?.total || 0,
    isLoading: contactsQuery.isLoading,
    refetch: contactsQuery.refetch,

    importHistory: importHistoryQuery.data || [],
    isHistoryLoading: importHistoryQuery.isLoading,

    stats: statsQuery.data || {},
    isStatsLoading: statsQuery.isLoading,

    // Actions
    createContact: createMutation.mutateAsync,
    updateContact: updateMutation.mutateAsync,
    deleteContact: deleteMutation.mutateAsync,
    bulkImport: bulkImportMutation.mutateAsync,
    deleteImport: deleteImportMutation.mutateAsync,

    isMutating: createMutation.isPending || updateMutation.isPending || deleteMutation.isPending || bulkImportMutation.isPending || deleteImportMutation.isPending
  };
};
